/**
 * ComparisonTable
 * ------------------------------------------------------------------
 * Tabella di confronto responsive basata su MUI <Table>.
 *
 * - Su mobile è scrollabile orizzontalmente (swipe).
 * - La prima colonna contiene le label delle righe.
 * - Le colonne successive rappresentano i servizi confrontati.
 *
 * Props:
 * @param caption   Titolo / caption principale della tabella (prima colonna, header)
 * @param columns   Lista dei titoli delle colonne servizio
 * @param rows      Righe della tabella con label + celle
 *
 * Celle supportate:
 * - { type: 'text', value: string } → testo descrittivo
 * - { type: 'check' }               → icona di check
 * - { type: 'dash' }                → trattino "–"
 * - { type: 'node', value: ReactNode } → contenuto custom
 *
 * NOTE:
 * - Il numero di `cells` per ogni riga DEVE corrispondere al numero di `columns`
 * - Pensata per confronti di feature / servizi / piani
 *
 * ESEMPIO DI UTILIZZO:
 * ------------------------------------------------------------------
 * <ComparisonTable
 *   caption="Confronto Servizi"
 *   columns={['Servizio A', 'Servizio B', 'Servizio C']}
 *   rows={[
 *     {
 *       label: 'Feature 1',
 *       cells: [
 *         { type: 'check' },
 *         { type: 'dash' },
 *         { type: 'check' }
 *       ]
 *     },
 *     {
 *       label: 'Feature 2',
 *       cells: [
 *         { type: 'text', value: 'Incluso' },
 *         { type: 'text', value: 'Non Incluso' },
 *         { type: 'text', value: 'Incluso' }
 *       ]
 *     },
 *     // altre righe...
 *   ]}
 * />
 * ------------------------------------------------------------------
 */
import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import {
  Box,
  SvgIcon,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material'

import theme from '@/styles/theme'

type ComparisonCell =
  | { type: 'text'; value: string }
  | { type: 'check' }
  | { type: 'dash' }
  | { type: 'node'; value: ReactNode }

type ComparisonRow = {
  label: string
  cells: ComparisonCell[] // deve avere stessa lunghezza di columns
}

type ComparisonTableProps = {
  caption: string
  columns: string[] // servizio 1, servizio 2, ...
  rows: ComparisonRow[]
}

const FIRST_COL_WIDTH = 'clamp(220px, 34vw, 520px)'

const renderCell = (cell: ComparisonCell) => {
  switch (cell.type) {
    case 'text':
      return (
        <Typography
          sx={{
            color: 'text.secondary',
            fontSize: theme.typography.textRegular.fontSize,
            lineHeight: 1.35
          }}
        >
          {cell.value}
        </Typography>
      )
    case 'check':
      return (
        <SvgIcon sx={{ fontSize: 40, color: 'text.secondary' }} aria-label="Disponibile">
          <use href="#ico-check" />
        </SvgIcon>
      )
    case 'dash':
      return (
        <Typography sx={{ color: 'text.secondary', fontSize: 24, lineHeight: 1 }}>–</Typography>
      )
    case 'node':
      return cell.value
    default:
      return null
  }
}

// Utility: clamp
const clamp = (n: number, min: number, max: number) => Math.max(min, Math.min(max, n))

export default function ComparisonTable({ caption, columns, rows }: ComparisonTableProps) {
  const colCount = columns.length

  // larghezza minima per far scorrere su mobile
  const minWidth = useMemo(() => {
    return 420 + colCount * 220
  }, [colCount])

  const scrollerRef = useRef<HTMLDivElement | null>(null)

  // Fake scrollbar state
  const [metrics, setMetrics] = useState({
    canScroll: false,
    scrollLeft: 0,
    scrollWidth: 1,
    clientWidth: 1
  })

  const updateMetrics = () => {
    const el = scrollerRef.current
    if (!el) return

    const { scrollLeft, scrollWidth, clientWidth } = el
    setMetrics({
      canScroll: scrollWidth > clientWidth + 1,
      scrollLeft,
      scrollWidth: Math.max(scrollWidth, 1),
      clientWidth: Math.max(clientWidth, 1)
    })
  }

  useEffect(() => {
    updateMetrics()

    const el = scrollerRef.current
    if (!el) return

    // aggiorna su scroll
    const onScroll = () => updateMetrics()
    el.addEventListener('scroll', onScroll, { passive: true })

    // aggiorna su resize (viewport / font / layout changes)
    const ro = new ResizeObserver(() => updateMetrics())
    ro.observe(el)

    // aggiorna se cambia contenuto tabella (columns/rows)
    // (il ResizeObserver copre già molti casi, ma teniamo anche questo)
    return () => {
      el.removeEventListener('scroll', onScroll)
      ro.disconnect()
    }
  }, [minWidth, columns.length, rows.length])

  // Thumb size/position (in px) dentro una track larga clientWidth
  const trackWidth = metrics.clientWidth
  const minThumbPx = 28

  const thumbWidth = metrics.canScroll
    ? clamp((metrics.clientWidth / metrics.scrollWidth) * trackWidth, minThumbPx, trackWidth)
    : trackWidth

  const maxScrollLeft = Math.max(metrics.scrollWidth - metrics.clientWidth, 1)
  const maxThumbLeft = Math.max(trackWidth - thumbWidth, 0)

  const thumbLeft = metrics.canScroll ? (metrics.scrollLeft / maxScrollLeft) * maxThumbLeft : 0

  const handleTrackClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = scrollerRef.current
    if (!el || !metrics.canScroll) return

    const rect = e.currentTarget.getBoundingClientRect()
    const clickX = e.clientX - rect.left

    // posiziona il thumb al centro del click
    const targetThumbLeft = clamp(clickX - thumbWidth / 2, 0, maxThumbLeft)
    const ratio = maxThumbLeft > 0 ? targetThumbLeft / maxThumbLeft : 0
    const targetScrollLeft = ratio * (metrics.scrollWidth - metrics.clientWidth)

    el.scrollTo({ left: targetScrollLeft, behavior: 'smooth' })
  }

  return (
    <Box sx={{ width: '100%' }}>
      <TableContainer
        ref={scrollerRef}
        component={Box}
        sx={{
          width: '100%',
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' }
        }}
        onScroll={updateMetrics}
      >
        <Table
          sx={{
            minWidth,
            borderCollapse: 'separate',
            borderSpacing: 0
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  py: 3,
                  pr: 4,
                  width: FIRST_COL_WIDTH,
                  maxWidth: FIRST_COL_WIDTH,
                  whiteSpace: 'normal',
                  overflowWrap: 'anywhere'
                }}
              >
                <Typography
                  component="div"
                  sx={{
                    fontSize: theme.typography.h6.fontSize,
                    fontWeight: 600,
                    lineHeight: 1.1
                  }}
                >
                  {caption}
                </Typography>
              </TableCell>

              {columns.map((col, i) => (
                <TableCell
                  key={col + i}
                  align="center"
                  sx={{
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    py: 3,
                    px: 2,
                    verticalAlign: 'top'
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: theme.typography.textRegular.fontSize,
                      fontWeight: 600,
                      color: 'primary.main'
                    }}
                  >
                    {col}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {rows.map((row, rIdx) => (
              <TableRow key={`${row.label}-${rIdx}`}>
                <TableCell
                  sx={{
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    py: 4,
                    pr: 4,
                    width: 420,
                    verticalAlign: 'middle'
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: theme.typography.textLarge.fontSize,
                      fontWeight: 500,
                      lineHeight: 1.2
                    }}
                  >
                    {row.label}
                  </Typography>
                </TableCell>

                {row.cells.map((cell, cIdx) => (
                  <TableCell
                    key={`${rIdx}-${cIdx}`}
                    align="center"
                    sx={{
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      py: 4,
                      px: 2,
                      verticalAlign: 'middle'
                    }}
                  >
                    {renderCell(cell)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Fake scrollbar */}
      {metrics.canScroll && (
        <Box
          sx={{
            mt: 1,
            width: '100%'
          }}
        >
          <Box
            role="scrollbar"
            aria-orientation="horizontal"
            aria-valuemin={0}
            aria-valuemax={maxScrollLeft}
            aria-valuenow={metrics.scrollLeft}
            onClick={handleTrackClick}
            sx={{
              height: 10,
              borderRadius: 999,
              background: 'rgba(0,0,0,0.08)',
              position: 'relative',
              cursor: 'pointer',
              touchAction: 'manipulation'
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: 1,
                bottom: 1,
                left: `${thumbLeft}px`,
                width: `${thumbWidth}px`,
                borderRadius: 999,
                background: 'rgba(0,0,0,0.28)'
              }}
            />
          </Box>
        </Box>
      )}
    </Box>
  )
}
